import { useMemo } from "react";

import { getTimeZones, TimeZone } from "@vvo/tzdb";
import Select from "react-select";

import { SelectFn, SelectOption } from "@/types/select";

const commonTimeZones = [
  // United States
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Phoenix",
  "America/Los_Angeles",
  "America/Anchorage",
  "America/Adak",
  "Pacific/Honolulu",
  "America/Puerto_Rico",
  // US territories
  "America/St_Thomas",
  "Pacific/Guam",
  // Europe
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Moscow",
  // Middle East
  "Asia/Jerusalem",
  // Asia/Pacific
  "Asia/Tokyo",
  "Australia/Sydney",
  "Pacific/Auckland",
];

function getSortedTimeZones() {
  const allTimeZones = getTimeZones();

  // Create a Map to store the index of each common time zone
  const commonTimeZonesOrder = new Map(
    commonTimeZones.map((tz, index) => [tz, index]),
  );

  // Sort the time zones
  const sortedTimeZones = allTimeZones.sort((a, b) => {
    const indexA = commonTimeZonesOrder.get(a.name);
    const indexB = commonTimeZonesOrder.get(b.name);

    if (indexA !== undefined && indexB !== undefined) {
      // Both are in commonTimeZones, sort by their order in that list
      return indexA - indexB;
    } else if (indexA !== undefined) {
      // Only a is in commonTimeZones, it should come first
      return -1;
    } else if (indexB !== undefined) {
      // Only b is in commonTimeZones, it should come first
      return 1;
    } else {
      // Neither is in commonTimeZones, sort by offset
      return a.rawOffsetInMinutes - b.rawOffsetInMinutes;
    }
  });

  return sortedTimeZones;
}

// Mapping of timezone names to friendly display names with regions
const friendlyTimezoneNames: Record<string, string> = {
  // US Timezones
  "America/New_York": "Eastern Time - US & Canada",
  "America/Chicago": "Central Time - US & Canada",
  "America/Denver": "Mountain Time - US & Canada",
  "America/Phoenix": "Mountain Time - Arizona",
  "America/Los_Angeles": "Pacific Time - US & Canada",
  "America/Anchorage": "Alaska Time - US",
  "America/Adak": "Hawaii-Aleutian Time - US",
  "Pacific/Honolulu": "Hawaii Time - US",
  "America/Puerto_Rico": "Atlantic Time - Puerto Rico",
  "America/St_Thomas": "Atlantic Time - US Virgin Islands",
  "Pacific/Guam": "Chamorro Time - Guam",

  // Europe
  "Europe/London": "Greenwich Mean Time - UK",
  "Europe/Paris": "Central European Time - France",
  "Europe/Berlin": "Central European Time - Germany",
  "Europe/Moscow": "Moscow Time - Russia",

  // Middle East & Asia
  "Asia/Jerusalem": "Israel Time - Israel",
  "Asia/Tokyo": "Japan Time - Japan",

  // Australia/Pacific
  "Australia/Sydney": "Australian Eastern Time - Australia",
  "Pacific/Auckland": "New Zealand Time - New Zealand",
};

function getFriendlyTimezoneName(timezoneName: string): string {
  // Check if we have a friendly name mapping
  if (friendlyTimezoneNames[timezoneName]) {
    return friendlyTimezoneNames[timezoneName];
  }

  // For unmapped timezones, extract the city/region name and format it nicely
  const parts = timezoneName.split("/");
  if (parts.length >= 2) {
    const region = parts[0].replace(/_/g, " ");
    const city = parts[parts.length - 1].replace(/_/g, " ");
    return `${city} Time - ${region}`;
  }

  return timezoneName;
}

function formatTimezoneLabel(tz: TimeZone): string {
  // Extract the offset part (e.g., "-07:00" or "+09:00")
  const offsetMatch = tz.currentTimeFormat.match(/^([+-]\d{2}:\d{2})/);
  const offset = offsetMatch ? offsetMatch[1] : "+00:00";

  // Use friendly name with UTC offset
  const friendlyName = getFriendlyTimezoneName(tz.name);
  return `${friendlyName} (${offset})`;
}

interface TimeZoneSelectorProps {
  value: string;
  onChange: SelectFn<SelectOption>;
  loading?: boolean;
  disabled?: boolean;
}

export function TimezoneSelector({
  value,
  onChange,
  loading = false,
  disabled = false,
}: TimeZoneSelectorProps) {
  const options = useMemo(
    () =>
      getSortedTimeZones().map((tz) => ({
        index: tz.rawOffsetInMinutes,
        value: tz.name,
        label: formatTimezoneLabel(tz),
        group: tz.group,
      })),
    [],
  );

  return (
    <Select
      isDisabled={disabled}
      isLoading={loading}
      value={options.find((o) => o.group.indexOf(value) > -1)}
      onChange={onChange}
      isSearchable={true}
      options={options}
      className="text-sm font-normal"
    />
  );
}
